import { Locale } from "@/Common/Models/Localization/Locale";
import { BaseOntarioObstetricalFileSubSection, BaseOntarioObstetricalFileSubSectionProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/Ontario/Subcomponent/BaseOntarioObstetricalFileSubSection";
import { GestationalAgeComponent } from "@/Patient/Components/MedicalNote/ObstetricalFile/Control/GestationalAgeComponent";
import { TextboxRenderOptions } from "@/Patient/Components/MedicalNote/ObstetricalFile/ControlRenderer/ObstetricalFileControlRendererOptions";
import { OntarioObstetricalFileFieldLimits } from "@/Patient/Components/MedicalNote/ObstetricalFile/Form/Ontario/OntarioObstetricalFileFieldLimits";
import { ObstetricsCalculator } from "@/Patient/Helpers/ObstetricsCalculator";
import { GestationalAge } from "@/Patient/Models/ObstetricalFile/GestationalAge";
import { OntarioObstetricalFileForm } from "@/Patient/Models/ObstetricalFile/Ontario/OntarioObstetricalFileForm";
import { PregnancyDatingMethod } from "@/Patient/Models/ObstetricalFile/Ontario/OPR1/PregnancyDatingMethodEnum";
import { OntarioObstetricalUltrasound } from "@/Patient/Models/ObstetricalFile/Ontario/OPR2/OntarioObstetricalUltrasound";

interface OntarioObstetricalFileUltrasoundProps extends BaseOntarioObstetricalFileSubSectionProps {
    patientDateOfBirth: Date;
}

class OntarioObstetricalFileUltrasoundState {
    public ultrasounds: OntarioObstetricalUltrasound[];
    public currentDate: Date;
}

class OntarioObstetricalFileUltrasound extends BaseOntarioObstetricalFileSubSection<OntarioObstetricalFileUltrasoundProps, OntarioObstetricalFileUltrasoundState> {
    private static readonly minUltrasoundRowCount = 4;
    private static readonly maxUltrasoundRowCount = 20;

    constructor(props: OntarioObstetricalFileUltrasoundProps) {
        super(props);
        this.state = this.buildState(this.props);
    }

    private buildState(props: BaseOntarioObstetricalFileSubSectionProps): OntarioObstetricalFileUltrasoundState {
        const state = new OntarioObstetricalFileUltrasoundState();
        state.ultrasounds = props.form.Opr2.Ultrasound?.map(p => new OntarioObstetricalUltrasound(p)) ?? [];
        state.currentDate = new Date();

        if (!state.ultrasounds?.any() || state.ultrasounds.length < OntarioObstetricalFileUltrasound.minUltrasoundRowCount)
            state.ultrasounds = this.initUltrasounds(OntarioObstetricalFileUltrasound.minUltrasoundRowCount);
        else if (props.readonly === false && state.ultrasounds.length < OntarioObstetricalFileUltrasound.maxUltrasoundRowCount && state.ultrasounds[state.ultrasounds.length - 1].hasValue())
            state.ultrasounds.push(new OntarioObstetricalUltrasound());
        else if (props.readonly === true && state.ultrasounds.length > OntarioObstetricalFileUltrasound.minUltrasoundRowCount && !state.ultrasounds[state.ultrasounds.length - 1].hasValue())
            state.ultrasounds.pop();

        return state;
    }

    public componentDidUpdate(prevProps: Readonly<OntarioObstetricalFileUltrasoundProps>): void {
        if (this.props.readonly !== prevProps.readonly) {
            this.setState(this.buildState(this.props));
        }
    }

    public render(): JSX.Element {
        return (
            <div className="section margin-md-top">
                <div className="row">
                    <div className="col-md-12">
                        {this.renderSubTitle("UltrasoundLabel")}
                        <table>
                            <thead>
                                <tr>
                                    <th rowSpan={2} className="text-bold col-medium">{cacheStore.GetString("DateLabel", null, Locale.en)}</th>
                                    <th rowSpan={2} className="text-bold col-medium">{cacheStore.GetString("GestationAgeAbbreviationLabel", null, Locale.en)}</th>
                                    <th rowSpan={2} className="text-bold col-medium">{cacheStore.GetString("UltrasoundAgeAbbreviationLabel", null, Locale.en)}</th>
                                    <th rowSpan={2} className="text-bold">{cacheStore.GetString("ResultLabel", null, Locale.en)}</th>
                                    <th rowSpan={2} className="col-medium ultrasound-no-border" />
                                </tr>
                            </thead>
                            <tbody>
                                {this.state.ultrasounds?.map((ultrasound, index) => this.renderUltrasoundRow(ultrasound, index))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    private renderUltrasoundRow(ultrasound: OntarioObstetricalUltrasound, index: number): JSX.Element {
        const fieldLimits = OntarioObstetricalFileFieldLimits.Page2;

        // Row index 11 and 12 are shorter on PDF side.
        const maxResultLength = index === 11 || index === 12
            ? fieldLimits.Ultrasound_Result_12.MaxChars
            : fieldLimits.Ultrasound_Result_LastRows.MaxChars;

        return (
            <tr key={ultrasound?.getId()} className="max-height-none">
                <td>
                    {this.controlRenderer.renderDateSelector({
                        value: ultrasound?.Date,
                        onChange: date => this.mutateUltrasoundRowState(
                            ultrasound?.getId(),
                            p => {
                                p.Date = date;
                                if (p.isUseForDatingDisabled())
                                    p.UseForDating = false;
                            },
                            true
                        ),
                        id: `Ultrasound_${ultrasound?.getId()}`,
                        className: "table-date-selector",
                        maxDate: this.state.currentDate,
                        ignoreInvalidCharacters: true
                    })}
                </td>
                <td>
                    <GestationalAgeComponent
                        id={`GestationWeeksAtBirth_${ultrasound.getId()}`}
                        gestationalAge={ultrasound.GestationalAge}
                        readonly={this.props.readonly}
                        onChange={value => this.mutateUltrasoundRowState(ultrasound.getId(), p => p.GestationalAge = value)}
                        locale={Locale.en}
                        controlRenderer={this.controlRenderer}
                    />
                </td>
                <td>
                    <GestationalAgeComponent
                        id={`UltrasoundAge_${ultrasound.getId()}`}
                        gestationalAge={ultrasound.UltrasoundAge}
                        readonly={this.props.readonly}
                        onChange={value => this.mutateUltrasoundRowState(ultrasound.getId(), p => {
                            p.UltrasoundAge = value;
                            if (p.isUseForDatingDisabled()) p.UseForDating = false;
                        })}
                        locale={Locale.en}
                        controlRenderer={this.controlRenderer}
                    />
                </td>
                <td className={ultrasound.rowIndex === 2 ? "ultrasound-third-row" : ""}>
                    {(ultrasound.rowIndex !== 2 && ultrasound.rowIndex !== 1) && (
                        <div className="field-value">
                            {this.controlRenderer.renderTextbox({
                                value: ultrasound.Result.Result,
                                onChange: value => this.mutateUltrasoundRowState(ultrasound.getId(), p => p.Result.Result = value.substr(0)),
                                maxLength: maxResultLength,
                                className: "table-textbox"
                            })}
                        </div>
                    )}
                    {ultrasound.rowIndex === 1 && (
                        <div className="field-value">
                            {this.renderUltrasoundLabelEllipsedCell(
                                "ObstetricalFile_NtUltrasoundLabel",
                                "",
                                {
                                    value: ultrasound.Result.NuchalTranslucencyUltrasound,
                                    onChange: value => this.mutateUltrasoundRowState(ultrasound.getId(), p => p.Result.NuchalTranslucencyUltrasound = value),
                                    maxLength: fieldLimits.Ultrasound_Result_2.MaxChars
                                }
                            )}
                        </div>
                    )}
                    {ultrasound.rowIndex === 2 && (
                        <table>
                            <tbody>
                                <tr>
                                    <td style={{ width: "35%" }}>
                                        {this.renderUltrasoundLabelEllipsedCell(
                                            "ObstetricalFile_AnatomyScanLabel",
                                            "100px",
                                            {
                                                value: ultrasound.Result.AnatomyScan,
                                                onChange: value => this.mutateUltrasoundRowState(ultrasound.getId(), p => p.Result.AnatomyScan = value),
                                                maxLength: fieldLimits.Ultrasound_Result_3_AnatomyScan.MaxChars
                                            }
                                        )}
                                    </td>
                                    <td style={{ width: "25%" }} className="ultrasound-divided-border">
                                        {this.renderUltrasoundLabelEllipsedCell(
                                            "ObstetricalFile_PlacentaLocationLabel",
                                            "100px",
                                            {
                                                value: ultrasound.Result.PlacentalLocation,
                                                onChange: value => this.mutateUltrasoundRowState(ultrasound.getId(), p => p.Result.PlacentalLocation = value),
                                                maxLength: fieldLimits.Ultrasound_Result_3_PlacentalLocation.MaxChars
                                            }
                                        )}
                                    </td>
                                    <td style={{ width: "40%" }} className="ultrasound-divided-border">
                                        {this.renderUltrasoundLabelEllipsedCell(
                                            "ObstetricalFile_SoftMarkersLabel",
                                            "200px",
                                            {
                                                value: ultrasound.Result.SoftMarkers,
                                                onChange: value => this.mutateUltrasoundRowState(ultrasound.getId(), p => p.Result.SoftMarkers = value),
                                                maxLength: fieldLimits.Ultrasound_Result_3_SoftMarkers.MaxChars
                                            }
                                        )}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </td>
                <td className="ultrasound-no-border multiple-inline-fields">
                    {this.controlRenderer.renderRadioButtonWithLabel(
                        `Ultrasouns_UseForDating_${ultrasound.getId()}`,
                        "useForDating",
                        cacheStore.GetString("ObstetricalFile_UseForDatingLabel", null, Locale.en),
                        ultrasound.UseForDating,
                        this.props.readonly || ultrasound.isUseForDatingDisabled(),
                        () => this.handleUseForDating(ultrasound.getId(), !ultrasound.UseForDating),
                        false,
                        "ultrasound-table-row use-for-dating",
                        "margin-none-bottom margin-xs-left"
                    )}
                </td>
            </tr>
        );
    }

    private renderUltrasoundLabelEllipsedCell(label: string, inputMaxWidth: string, options: TextboxRenderOptions): JSX.Element {
        return this.props.readonly
            ? <span className="field-text" style={{ lineHeight: 0 }}><span style={{ color: "#7F7F7F" }}>{this.getString(label)}</span> {options.value}</span>
            : (
                <div className="flex-display flex-align-items-center">
                    <span className="ellipsis-auto margin-xs-right" style={{ color: "#7F7F7F" }} title={this.getString(label)}>{this.getString(label)}</span>
                    <div className="flex-grow-1" style={{ maxWidth: inputMaxWidth }}>{this.controlRenderer.renderTextbox({ ...options, className: "table-textbox" })}</div>
                </div>
            );
    }

    private initUltrasounds(number: number): OntarioObstetricalUltrasound[] {
        let ultrasound: OntarioObstetricalUltrasound[] = [];
        for (let i = 0; i < number; ++i) {
            ultrasound[i] = new OntarioObstetricalUltrasound({ rowIndex: i });
        }
        return ultrasound;
    }

    private handleUseForDating(ultrasoundId: number, useForDating: boolean): void {
        this.mutateState(
            s => s.ultrasounds = s.ultrasounds.mutate(p => p.UseForDating = p.getId() === ultrasoundId ? useForDating : false),
            () => this.mutateUltrasoundRowState(ultrasoundId, () => { }, true)
        );
    }

    private mutateUltrasoundRowState(id: number, mutation: (state: OntarioObstetricalUltrasound) => void, updateForm: boolean = false): void {
        const currentUltrasound = this.state.ultrasounds.firstOrDefault(p => p.getId() === id);
        let ultrasounds: OntarioObstetricalUltrasound[] = [];
        let deletedCount = 0;

        this.mutateState(
            s => {
                s.ultrasounds = s.ultrasounds.mutate(
                    p => mutation(p),
                    p => p.getId() === id
                );

                let lastRowIndex = s.ultrasounds.length - 1;

                while (s.ultrasounds.length > OntarioObstetricalFileUltrasound.minUltrasoundRowCount && !s.ultrasounds[lastRowIndex].hasValue()) {
                    s.ultrasounds.splice(lastRowIndex, 1);
                    lastRowIndex--;
                    deletedCount++;
                }

                ultrasounds = s.ultrasounds.map(p => new OntarioObstetricalUltrasound(p));

                if (s.ultrasounds.length < OntarioObstetricalFileUltrasound.maxUltrasoundRowCount && s.ultrasounds[s.ultrasounds.length - 1].hasValue())
                    s.ultrasounds.push(new OntarioObstetricalUltrasound());
            },
            () => {
                (deletedCount > 1 || updateForm
                    ? this.props.mutateModelStateWithFormUpdate
                    : this.props.mutateModelState
                )(s => {
                    s.Opr2.Ultrasound = ultrasounds;
                    this.updateEstimatedDateOfBirthBasedOnUltrasound(s, currentUltrasound);
                });
            }
        );
    }

    private getDatingMethod(ultrasoundAge: GestationalAge): PregnancyDatingMethod {
        const firstSecondTrimesterDay = 91;
        const firstThirdTrimesterDay = 182;

        if (ultrasoundAge.TotalNumberOfDays >= firstSecondTrimesterDay && ultrasoundAge.TotalNumberOfDays < firstThirdTrimesterDay)
            return PregnancyDatingMethod.SecondTrimesterUltrasound;

        else if (ultrasoundAge.TotalNumberOfDays < firstSecondTrimesterDay)
            return PregnancyDatingMethod.FirstTrimesterUltrasound;

        return null;
    }

    private updateEstimatedDateOfBirthBasedOnUltrasound(form: OntarioObstetricalFileForm, ultrasound: OntarioObstetricalUltrasound): void {
        if (!ultrasound.UseForDating)
            return;

        form.Common.FinalEstimatedDateOfBirth = ObstetricsCalculator.calculateDateOfBirthBasedOnUltrasound(ultrasound.Date, ultrasound.UltrasoundAge.TotalNumberOfDays) ?? form.Common.FinalEstimatedDateOfBirth;
        form.Opr1.PregnancySummary.DatingMethodValue = this.getDatingMethod(ultrasound.UltrasoundAge) ?? form.Opr1.PregnancySummary.DatingMethodValue;

        form.updateSubsequentVisitGestationalAge(this.props.contributionId);
        form.updateGestationalAgeAtBirth();
        form.updateAgeAtEstimatedDateOfBirth(this.props.patientDateOfBirth, form.Common.FinalEstimatedDateOfBirth);
    }
}

export { OntarioObstetricalFileUltrasound };
